import { useEffect, useRef } from 'react'
import { QRCodeElement } from '@/types'
import { useSettingsStore } from '@/stores/settingsStore'
import { useLabelStore } from '@/stores/labelStore'
import QRCode from 'qrcode'

interface QRCodeElementPreviewProps {
  element: QRCodeElement
  zoom: number
  mmToPx: number
}

const errorCorrectionMap: Record<string, 'L' | 'M' | 'Q' | 'H'> = {
  'L': 'L',
  'M': 'M',
  'Q': 'Q',
  'H': 'H',
}

export function QRCodeElementPreview({ element, zoom, mmToPx }: QRCodeElementPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const { variableData } = useSettingsStore()
  const { label } = useLabelStore()

  // Get display content
  let content = element.content
  if (element.isVariable && element.variableName) {
    content = variableData[element.variableName] || element.content
  }

  // QR code size calculation:
  // In ZPL, magnification sets the size of each module in dots
  // A typical QR code has ~21-25 modules for short content
  // Size in dots = magnification * number_of_modules
  // Size in mm = size_in_dots / (dpi / 25.4)
  const dotsPerMm = label.dpi / 25.4
  // Estimate ~25 modules for typical content, then convert to mm then to px
  const estimatedModules = 25
  const sizeInDots = element.magnification * estimatedModules
  const sizeInMm = sizeInDots / dotsPerMm
  const size = Math.max(20, sizeInMm * mmToPx * zoom)

  useEffect(() => {
    if (!canvasRef.current || !content) return

    QRCode.toCanvas(canvasRef.current, content, {
      width: size,
      margin: 0,
      errorCorrectionLevel: errorCorrectionMap[element.errorCorrection] || 'M',
    }).catch(() => {
      // Error generating QR code - show placeholder
      const ctx = canvasRef.current?.getContext('2d', { willReadFrequently: true })
      if (ctx && canvasRef.current) {
        canvasRef.current.width = size
        canvasRef.current.height = size
        ctx.fillStyle = '#f0f0f0'
        ctx.fillRect(0, 0, size, size)
        ctx.fillStyle = '#666'
        ctx.font = `${10 * zoom}px Arial`
        ctx.textAlign = 'center'
        ctx.fillText('QR', size / 2, size / 2)
      }
    })
  }, [content, size, element.errorCorrection, zoom])

  return <canvas ref={canvasRef} />
}
